import * as React from 'react'

import useI18n from '@store/i18n/useI18n'
import useReducer from '@store/useReducer'
import * as AccountStore from '@store/account/account'
import * as AuthStore from '@store/auth/auth'

import styled from '@theme/styled-components'
import Button from '@components/button/button'
import Card from '@components/card/card'
import apis from '@app/api/api'

import OAuthConfig from '@app/config'
import oauth from '@utils/OAuth2'
import Logger from '@utils/Logger'

import Swal from 'sweetalert2'

interface Props {
  navigation: Navigation
  auto: boolean
  authInfo?: { code: string; mode: AuthMode }
}
const Login = ({ authInfo, navigation, auto }: Props) => {
  const i18n = useI18n()
  const [screenHeight, setScreenHeight] = React.useState<number>(window.innerHeight)
  const [authLoading, setAuthLoading] = React.useState<AuthMode | undefined>(undefined)

  const account = useReducer(AccountStore.store, (s) => s.account)
  const tokens = useReducer(AuthStore.store, (s) => s.OAuth)

  React.useLayoutEffect(() => {
    const updateDimensions = () => {
      setScreenHeight(window.innerHeight)
    }
    window.addEventListener('resize', updateDimensions)
    return () => window.removeEventListener('resize', updateDimensions)
  }, [])

  const getUserInfo = (tokens: OauthCredentials | undefined) => {
    if (tokens) {
      apis.user
        .login()
        .then((user) => AccountStore.actions.connectUser({ ...user, authmode: tokens.auth_mode }))
        .catch((err) => {
          if (err.data?.message.includes('Unknown active user')) {
            Swal.fire({
              title: i18n.t('common.warning'),
              text: i18n.t('screens.login.unknownUser'),
              confirmButtonText: i18n.t('common.ok'),
              confirmButtonColor: 'green',
            })
          }
          AuthStore.actions.resetTokens()
          Logger.info('ERROR ON LOGGING CURRENT USER : ', err)
        })
    }
  }

  React.useEffect(() => {
    if (authInfo) {
      setAuthLoading(authInfo.mode)
      oauth
        .exchangeCode(authInfo.mode, authInfo.code, getUserInfo)
        .then(() => setAuthLoading(undefined))
        .catch(() => setAuthLoading(undefined))
    }
  }, [])

  React.useEffect(() => {
    if (auto) {
      if (window.location.href.startsWith(OAuthConfig.AUTH_CONFIG['INTERNAL'].loginUrl)) {
        setAuthLoading('INTERNAL')
        oauth.login('INTERNAL')
      } else {
        setAuthLoading('EXTERNAL')
        oauth.login('EXTERNAL')
      }
    }
  }, [])

  const connectUser = (mode: AuthMode) => {
    if (window.location.href.startsWith(OAuthConfig.AUTH_CONFIG[mode].loginUrl)) {
      setAuthLoading(mode)
      oauth.login(mode)
    } else {
      if (mode === 'INTERNAL') {
        window.open(`${OAuthConfig.AUTH_CONFIG['INTERNAL'].loginUrl}/auto`, '_self')
      } else {
        window.open(`${OAuthConfig.AUTH_CONFIG['EXTERNAL'].loginUrl}/auto`, '_self')
      }
    }
  }

  const changeAccount = () => AccountStore.actions.changeAccount()

  return (
    <Container screenHeight={screenHeight}>
      <LogoContainer>
        <Logo src={require('images/logo_baseline.png').default} />
      </LogoContainer>
      <AuthContainer>
        <ItemContainer>
          <Title>{i18n.t('screens.login.title')}</Title>
        </ItemContainer>
        {account && !tokens ? (
          <>
            <Card>
              <>
                <ItemContainer>
                  <UserName>{account.firstname + ' ' + account.lastname}</UserName>
                  <UserRole>{i18n.t(`userRoles.${account.role}`)}</UserRole>
                </ItemContainer>
                <Button
                  title={i18n.t('screens.login.user.connect')}
                  status="active"
                  type="navigation"
                  size="medium"
                  onClick={() => connectUser(account.authmode)}
                />
              </>
            </Card>
            <ItemContainer>
              <Button
                title={i18n.t('screens.login.user.change')}
                status="active"
                type="classic"
                size="medium"
                onClick={changeAccount}
              />
            </ItemContainer>
          </>
        ) : (
          <>
            <ItemContainer>
              <Button
                title={i18n.t('screens.login.initial.internal')}
                status={authLoading ? (authLoading === 'INTERNAL' ? 'loading' : 'disabled') : 'active'}
                type="action"
                onClick={() => connectUser('INTERNAL')}
              />
            </ItemContainer>
            <ItemContainer>
              <Button
                title={i18n.t('screens.login.initial.external')}
                status={authLoading ? (authLoading === 'EXTERNAL' ? 'loading' : 'disabled') : 'active'}
                type="navigation"
                onClick={() => connectUser('EXTERNAL')}
              />
            </ItemContainer>
          </>
        )}
      </AuthContainer>
      <LoginBackground src={require('images/login_background.png').default} />
    </Container>
  )
}

export default Login

const Container = styled('div')<{ screenHeight: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: ${(props) => props.screenHeight}px;
`
const LogoContainer = styled('div')`
  display: flex;
  flex: 1;
`
const Logo = styled('img')`
  max-width: 200px;
  object-fit: contain;
`
const Title = styled('div')`
  ${(props) => props.theme.fonts.title};
`
const AuthContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  flex: 2;
`
const ItemContainer = styled('div')`
  margin: 15px;
`
const LoginBackground = styled('img')`
  top: 0;
  right: 0;
  z-index: -1;
  height: 100%;
  opacity: 0.75;
  object-fit: cover;
  position: absolute;
`
const UserName = styled('div')`
  text-align: center;
  ${(props) => props.theme.fonts.boldLabel};
`
const UserRole = styled('div')`
  text-align: center;
  ${(props) => props.theme.fonts.label};
`
